import type { TelemetryDiInput } from "../schema/di.js";
import type { TelemetryConfigInput } from "../schema/config.js";
import type { ProviderConfig } from "../schema/provider.js";
import type { TelemetryRuntimeInput } from "../schema/runtime.js";

/**
 * Framework-agnostic module config (core).
 * For tree-shaking: do not put `framework` here — use `@komerce/telemetry/adapter/*` platforms.
 */
export interface TelemetryModuleInput {
  appId: string;
  environment?: TelemetryConfigInput["environment"];
  release?: string;
  sampleRate?: number;
  captureGlobalErrors?: boolean;
  breadcrumbs?: TelemetryConfigInput["breadcrumbs"];
  providers?: ProviderConfig[];
  runtime?: TelemetryRuntimeInput;
  /** Create both client + server instances (SSR) */
  ssr?: boolean;
  injection?: TelemetryDiInput["injection"];
  context?: TelemetryConfigInput["context"];
  client?: Partial<TelemetryConfigInput>;
  server?: Partial<TelemetryConfigInput>;
}

export type SetupTelemetryInput = TelemetryModuleInput | TelemetryDiInput;

export function isFlatModuleInput(
  input: SetupTelemetryInput,
): input is TelemetryModuleInput {
  return "appId" in input && !("config" in input);
}

export function normalizeSetupInput(input: SetupTelemetryInput): TelemetryDiInput {
  if (!isFlatModuleInput(input)) {
    return input;
  }

  const {
    appId,
    environment,
    release,
    sampleRate,
    captureGlobalErrors,
    breadcrumbs,
    providers,
    runtime,
    ssr,
    injection,
    context,
    client,
    server,
  } = input;

  const sharedConfig: TelemetryConfigInput = {
    appId,
    environment,
    release,
    sampleRate,
    captureGlobalErrors,
    breadcrumbs,
    providers,
    context,
    runtime,
  };

  return {
    framework: "generic",
    config: sharedConfig,
    client: client ? { ...sharedConfig, ...client, appId } : undefined,
    server: server ? { ...sharedConfig, ...server, appId } : undefined,
    runtime,
    ssr: ssr ?? false,
    injection,
  };
}
